import React, { useEffect, useMemo, useState } from 'react';
import { getStickerUrl } from '../stickers/StickerDesigns';
import type {
  NewSlapOnchain,
  OnchainPaymentMethod,
  PendingSlapList,
  StickerDesign,
} from '../utils/Primitives';
import { useReadContract } from 'wagmi';
import { formatEther, zeroAddress } from 'viem';
import { abi } from './StickerChainAbi';
const StickerChainContractAddress = process.env
  .NEXT_PUBLIC_STICKER_CHAIN_CONTRACT_ADDRESS as `0x${string}` | null;

type SlapperButtonSectionProps = {
  sticker: StickerDesign | null;
  stickerDescription?: string | null;
  onStickerInfoClick?: () => void;
  preCheckWarningsContent?: React.ReactNode;
  hasPendingSlap: boolean;
  hasConnectedWallet: boolean;
  currentSlapSize: number;
  onSlapSizeChange: (size: number) => void;
  onPickStickers: () => void;
  onSlapAction: () => void;
  onCancelDraft: () => void;
  isSlapDisabled: boolean;
  pendingSlaps: PendingSlapList | null;
  instructionText?: string;
  showPickStickers?: boolean;
  className?: string;
  stickerLabel?: string;
  marginTopClassName?: string;
  helperText?: string;
  helperTextClassName?: string;
  splitDesktop?: boolean;
  helperTextDesktopPosition?: 'below' | 'above-controls';
};

export default function SlapperButtonSection({
  sticker,
  stickerDescription,
  onStickerInfoClick,
  preCheckWarningsContent,
  hasPendingSlap,
  hasConnectedWallet,
  currentSlapSize,
  onSlapSizeChange,
  onPickStickers,
  onSlapAction,
  onCancelDraft,
  isSlapDisabled,
  pendingSlaps,
  instructionText,
  showPickStickers = true,
  className,
  stickerLabel = 'Current sticker',
  marginTopClassName = 'mt-4',
  helperText,
  helperTextClassName,
  splitDesktop = false,
  helperTextDesktopPosition = 'below',
}: SlapperButtonSectionProps) {
  const MAX_EXPANDED_DESCRIPTION_CHARS = 280;
  const stickerImage = sticker ? getStickerUrl(sticker) : null;
  const stickerName = sticker?.name || 'No sticker selected';
  const stickerIdLabel = sticker?.id != null ? `#${sticker.id}` : '';
  const hasStickerId = Boolean(stickerIdLabel);
  const publisherEns = sticker?.currentPublisherEns;
  const publisherAddress = sticker?.currentPublisher;
  const publisherLabel = useMemo(() => {
    if (publisherEns && publisherEns.trim()) {
      return publisherEns;
    }
    if (!publisherAddress || !publisherAddress.startsWith('0x')) {
      return null;
    }
    const compact = publisherAddress.slice(2);
    if (compact.length < 6) {
      return publisherAddress;
    }
    return `${compact.slice(0, 3).toUpperCase()}...${compact.slice(-3).toUpperCase()}`;
  }, [publisherEns, publisherAddress]);
  const trimmedDescription = (stickerDescription ?? '').trim();
  const descriptionIsLimited =
    trimmedDescription.length > MAX_EXPANDED_DESCRIPTION_CHARS;
  const expandedDescription = descriptionIsLimited
    ? `${trimmedDescription.slice(0, MAX_EXPANDED_DESCRIPTION_CHARS).trimEnd()}...`
    : trimmedDescription;
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const canToggleDescription =
    trimmedDescription.length > 0 && (descriptionIsLimited || trimmedDescription.length > 60);
  const stickerLabelWithId = stickerIdLabel
    ? stickerLabel === 'Sticker'
      ? `${stickerLabel} ${stickerIdLabel}`
      : `${stickerLabel}: ${stickerIdLabel}`
    : stickerLabel;
  const [totalCosts, setTotalCosts] = useState<OnchainPaymentMethod[]>([]);
  const slapSpecs = useMemo<NewSlapOnchain[]>(() => {
    if (!pendingSlaps) {
      return [];
    }
    return pendingSlaps.map((item) => ({
      placeId: BigInt(item.place.id),
      stickerId: BigInt(item.sticker.id),
      size: BigInt(item.size ? item.size : 1),
    }));
  }, [pendingSlaps]);

  const enableRead =
    slapSpecs.length > 0 && StickerChainContractAddress !== null;

  const {
    data: costOfSlapsData,
    isFetched: costOfSlapsIsFetched,
    isError: costOfSlapsIsError,
  } = useReadContract({
    abi,
    address: StickerChainContractAddress || '0x0',
    functionName: 'costOfSlaps',
    query: { enabled: enableRead },
    args: [zeroAddress, slapSpecs, []],
  });

  useEffect(() => {
    if (!enableRead) {
      setTotalCosts([]);
      return;
    }
    if (costOfSlapsIsFetched && costOfSlapsData && !costOfSlapsIsError) {
      const paymentTotals: OnchainPaymentMethod[] = costOfSlapsData.map(
        (item) => ({
          total: item.total,
          paymentMethodId: item.paymentMethodId,
        }),
      );
      setTotalCosts(paymentTotals);
    }
  }, [enableRead, costOfSlapsIsFetched, costOfSlapsData, costOfSlapsIsError]);

  const slapCostLabel = useMemo(() => {
    if (totalCosts.length === 0) {
      return 'Slap Cost: --';
    }
    const baseCost =
      totalCosts.find((cost) => cost.paymentMethodId === BigInt(0)) ??
      totalCosts[0];
    const symbol = baseCost.paymentMethodId === BigInt(0) ? 'ETH' : 'tokens';
    return `Slap Cost: ${formatEther(baseCost.total)} ${symbol}`;
  }, [totalCosts]);

  return (
    <div
      className={[
        marginTopClassName,
        'flex w-full flex-col items-center gap-3',
        splitDesktop ? 'md:flex-row md:items-start md:gap-4' : '',
        className,
      ].filter(Boolean).join(' ')}
    >
      <div
        className={[
          'flex w-full flex-col',
          splitDesktop ? 'md:w-auto md:min-w-[240px]' : '',
        ].join(' ')}
      >
        <div
          className={[
            'flex w-full flex-col gap-2 rounded-2xl',
            'border border-slate-200 bg-slate-50 px-3 py-2',
          ].join(' ')}
        >
          {instructionText && (
            <div className="text-xs text-slate-600 leading-snug">
              {instructionText}
            </div>
          )}
          <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
            {stickerLabelWithId}
          </div>
          <div className="flex w-full items-center justify-between gap-2">
            <button
              type="button"
              onClick={onStickerInfoClick}
              className="flex min-w-0 flex-1 items-center gap-3 text-left"
              disabled={!onStickerInfoClick}
            >
              <div
                className={[
                  'h-14 w-14 md:h-12 md:w-12 overflow-hidden rounded-xl border',
                  'border-slate-200 bg-white shrink-0',
                ].join(' ')}
              >
                {stickerImage ? (
                  <img
                    src={stickerImage}
                    alt={stickerName}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full" aria-hidden="true" />
                )}
              </div>
              <div className="min-w-0 flex flex-col text-sm leading-snug">
                <span className="sticker-name-two-line text-[13px] md:text-sm font-semibold text-slate-800">
                  {stickerName}
                </span>
                {publisherLabel && (
                  <span
                    className="max-w-[180px] truncate text-[11px] text-slate-500"
                    title={publisherLabel}
                  >
                    {publisherLabel}
                  </span>
                )}
                {trimmedDescription && (
                  <span
                    role={canToggleDescription ? 'button' : undefined}
                    tabIndex={canToggleDescription ? 0 : -1}
                    aria-expanded={canToggleDescription ? isDescriptionExpanded : undefined}
                    title={canToggleDescription ? 'Tap to expand/collapse' : undefined}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!canToggleDescription) {
                        return;
                      }
                      setIsDescriptionExpanded((prev) => !prev);
                    }}
                    onKeyDown={(event) => {
                      if (!canToggleDescription) {
                        return;
                      }
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setIsDescriptionExpanded((prev) => !prev);
                      }
                    }}
                    className={[
                      'inline-block self-start max-w-full text-[11px] text-slate-500 break-words',
                      isDescriptionExpanded ? '' : 'truncate',
                      canToggleDescription
                        ? 'cursor-pointer hover:text-slate-700'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {isDescriptionExpanded ? expandedDescription : trimmedDescription}
                  </span>
                )}
              </div>
            </button>
            {!hasPendingSlap && showPickStickers && (
              <div className="flex justify-end">
                <button
                  onClick={onPickStickers}
                  className={[
                    'whitespace-nowrap rounded-full bg-amber-300 px-3 py-1.5',
                    'text-[11px] font-semibold text-slate-800 hover:bg-amber-200',
                    'md:px-4 md:py-2 md:text-xs',
                    'transition-colors',
                  ].join(' ')}
                >
                  Pick Stickers!
                </button>
              </div>
            )}
          </div>
        </div>
        {helperText && !hasPendingSlap && (
          <div
            className={[
              'mt-2 text-sm text-slate-500 leading-snug',
              splitDesktop && helperTextDesktopPosition === 'above-controls'
                ? 'md:hidden'
                : '',
              helperTextClassName,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {helperText}
          </div>
        )}
      </div>

      <div
        className={[
          'w-full',
          splitDesktop
            ? 'md:w-[260px] md:min-w-[260px] md:shrink-0 md:rounded-2xl md:bg-slate-50 md:px-3 md:py-3'
            : '',
        ].join(' ')}
      >
        {helperText && !hasPendingSlap &&
          splitDesktop &&
          helperTextDesktopPosition === 'above-controls' && (
            <div
              className={[
                'hidden md:block md:mb-3 text-sm text-slate-500 leading-snug',
                helperTextClassName,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {helperText}
            </div>
          )}
        {hasPendingSlap ? (
          <>
            <div
              className={[
                'w-full rounded-2xl border border-amber-200/55',
                'ring-1 ring-amber-200/20 bg-gradient-to-r',
                'from-amber-500/25 via-slate-900/20 to-orange-500/25',
                'px-4 py-3 text-xs font-semibold text-amber-200 slap-warning',
              ].join(' ')}
            >
              {hasConnectedWallet ? (
                <div>To complete this slap, commit it to the Base blockchain.</div>
              ) : (
                <>
                  <div>Commit this slap on Base with your crypto wallet.</div>
                  {/* <div className="mt-2">
                    Need a wallet? Try{' '}
                    <a
                      href="https://rabby.io/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={[
                        'underline slap-warning-link',
                        'transition',
                      ].join(' ')}
                    >
                      Rabby
                    </a>
                    ,{' '}
                    <a
                      href="https://www.coinbase.com/wallet/downloads"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={[
                        'underline slap-warning-link',
                        'transition',
                      ].join(' ')}
                    >
                      Base App
                    </a>{' '}
                    or{' '}
                    <a
                      href="https://rainbow.me/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={[
                        'underline slap-warning-link',
                        'transition',
                      ].join(' ')}
                    >
                      Rainbow
                    </a>
                    .
                  </div> */}
                </>
              )}
            </div>
            {preCheckWarningsContent && (
              <div className="mt-3 w-full">
                {preCheckWarningsContent}
              </div>
            )}
            <div className="mt-4 flex w-full gap-3">
              <button
                onClick={onCancelDraft}
                className={[
                  'w-1/2 rounded-full border border-slate-300 bg-white',
                  'px-4 py-2 text-xs font-semibold text-slate-900',
                  'hover:bg-slate-100 transition',
                ].join(' ')}
              >
                Cancel
              </button>
              <button
                onClick={onSlapAction}
                className={[
                  'w-1/2 rounded-full bg-gradient-to-r from-red-600',
                  'to-orange-400 px-4 py-2 text-xs font-semibold',
                  'text-white transition',
                ].join(' ')}
              >
                Commit Slap
              </button>
            </div>
            <div className="mt-3 w-full min-h-[16px] text-center text-xs slap-fee-label">
              {slapCostLabel}
            </div>
          </>
        ) : (
          <div className="flex w-full items-stretch gap-3">
            <div className="relative w-[36%] min-w-[120px]">
              <select
                value={currentSlapSize}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  if (Number.isFinite(next) && next >= 1 && next <= 10) {
                    onSlapSizeChange(next);
                  }
                }}
                className={[
                  'w-full appearance-none rounded-full border border-slate-200',
                  'bg-white px-3 py-2 pr-9 text-xs text-slate-700',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500',
                ].join(' ')}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((size) => (
                  <option key={size} value={size}>
                    Size {size}
                  </option>
                ))}
              </select>
              <svg
                aria-hidden="true"
                viewBox="0 0 20 20"
                className={[
                  'pointer-events-none absolute right-3 top-1/2 h-4 w-4',
                  '-translate-y-1/2 text-slate-500',
                ].join(' ')}
                fill="currentColor"
              >
                <path d="M5.25 7.5L10 12.25 14.75 7.5H5.25z" />
              </svg>
            </div>
            <button
              onClick={onSlapAction}
              disabled={isSlapDisabled}
              className={[
                'w-[64%] rounded-full bg-blue-600 px-4 py-2',
                'text-xs font-semibold text-white hover:bg-blue-700',
                'disabled:cursor-not-allowed disabled:opacity-50 transition',
              ].join(' ')}
            >
              Slap!
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
